import { Modal, Typography, Radio, Select } from 'antd';
import { UserOutlined, CheckCircleOutlined, TagOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '~/components';
import colors from '~/constants/colors';
import { AgentLead } from '~/types/AgentLead';
import { useSales } from '~/controllers/sales';

interface SelectedLeadsBulkEditModalProps {
  open: boolean;
  onClose: () => void;
  selectedLeads: AgentLead[];
  onBulkEdit: (
    leads: AgentLead[],
    updates: { assigneeId?: string; result?: string }
  ) => Promise<void>;
}

type BulkActionType = 'assignee' | 'result';

const RESULT_OPTIONS = [
  'Not Handled Yet',
  'Merchant not Reachable',
  'Continue with Self-Onboarding with Support',
  'Continue with Self-Onboarding without Support',
  'Close Lead & Log the Reason',
];

const SelectedLeadsBulkEditModal: React.FC<SelectedLeadsBulkEditModalProps> = ({
  open,
  onClose,
  selectedLeads,
  onBulkEdit,
}) => {
  const { t } = useTranslation('sales');
  const sales = useSales();
  const [actionType, setActionType] = useState<BulkActionType>('assignee');
  const [selectedAssignee, setSelectedAssignee] = useState<string | undefined>(undefined);
  const [selectedResult, setSelectedResult] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const handleContinue = async () => {
    if (actionType === 'assignee' && !selectedAssignee) {
      return;
    }
    if (actionType === 'result' && !selectedResult) {
      return;
    }

    setLoading(true);

    try {
      if (actionType === 'assignee' && selectedAssignee) {
        await onBulkEdit(selectedLeads, { assigneeId: selectedAssignee });
      } else if (actionType === 'result' && selectedResult) {
        await onBulkEdit(selectedLeads, { result: selectedResult });
      }
      handleClose();
    } catch (error) {
      console.error('Bulk action error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActionType('assignee');
    setSelectedAssignee(undefined);
    setSelectedResult(undefined);
    onClose();
  };

  const selectedCount = selectedLeads.length;

  const assigneeOptions = sales.data?.map(({ id, fullName }) => ({
    label: fullName,
    value: id,
  }));

  const resultOptions = RESULT_OPTIONS.map((result) => ({
    label: result,
    value: result,
  }));

  const isContinueDisabled =
    (actionType === 'assignee' && !selectedAssignee) ||
    (actionType === 'result' && !selectedResult);

  return (
    <StyledModal open={open} onCancel={handleClose} footer={null} width={550} centered closable>
      <ModalContent>
        <Typography.Title level={5}>{t('Bulk Action for Selected Leads')}</Typography.Title>

        <SelectedCountInfo>
          <Typography.Text>
            {t('{{count}} lead(s) selected', { count: selectedCount })}
          </Typography.Text>
        </SelectedCountInfo>

        <ActionLabel>
          <Typography.Text strong>{t('Select Action')}</Typography.Text>
        </ActionLabel>

        <StyledRadioGroup value={actionType} onChange={(e) => setActionType(e.target.value)}>
          <Radio value="assignee">
            <RadioContent>
              <UserOutlined />
              <span>{t('Change Assignee')}</span>
            </RadioContent>
          </Radio>
          <Radio value="result">
            <RadioContent>
              <TagOutlined />
              <span>{t('Change Result')}</span>
            </RadioContent>
          </Radio>
        </StyledRadioGroup>

        {actionType === 'assignee' && (
          <DropdownSection>
            <DropdownLabel>
              <Typography.Text strong>{t('Select New Assignee')}</Typography.Text>
            </DropdownLabel>
            <Select
              id="bulkAssigneeSelect"
              placeholder={t('Choose assignee')}
              value={selectedAssignee}
              onChange={setSelectedAssignee}
              options={assigneeOptions}
              loading={sales.isLoading}
              disabled={sales.isLoading}
              style={{ width: '100%' }}
              size="large"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </DropdownSection>
        )}

        {actionType === 'result' && (
          <DropdownSection>
            <DropdownLabel>
              <Typography.Text strong>{t('Select New Result')}</Typography.Text>
            </DropdownLabel>
            <Select
              id="bulkResultSelect"
              placeholder={t('Choose result')}
              value={selectedResult}
              onChange={setSelectedResult}
              options={resultOptions}
              style={{ width: '100%' }}
              size="large"
            />
          </DropdownSection>
        )}

        <ButtonContainer>
          <Button
            id="bulkActionContinueButton"
            type="primary"
            onClick={handleContinue}
            loading={loading}
            disabled={isContinueDisabled}
          >
            {t('Continue')}
          </Button>
        </ButtonContainer>
      </ModalContent>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    padding: 24px;
  }

  .ant-modal-close {
    top: 16px;
    right: 16px;
  }

  .ant-modal-close-x {
    width: 32px;
    height: 32px;
    line-height: 32px;
    color: ${colors.grey[500]};
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  .ant-typography-title {
    margin: 0;
    color: ${colors.blue[1100]};
    text-align: center;
    font-size: 16px;
  }
`;

const SelectedCountInfo = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 16px;
  background-color: ${colors.blue[50]};
  border: 1px solid ${colors.blue[200]};
  border-radius: 6px;

  .ant-typography {
    font-size: 14px;
    font-weight: 500;
    color: ${colors.blue[600]};
  }
`;

const ActionLabel = styled.div`
  .ant-typography {
    font-size: 14px;
    color: ${colors.textColor};
  }
`;

const StyledRadioGroup = styled(Radio.Group)`
  display: flex;
  flex-direction: column;
  gap: 12px;

  .ant-radio-wrapper {
    padding: 16px;
    border: 1px solid ${colors.borderColorBase};
    border-radius: 6px;
    margin: 0;
    transition: all 0.3s ease;

    &:hover {
      border-color: ${colors.blue[500]};
      background-color: ${colors.blue[50]};
    }

    &.ant-radio-wrapper-checked {
      border-color: ${colors.blue[500]};
      background-color: ${colors.blue[50]};
    }

    .ant-radio-checked .ant-radio-inner {
      border-color: ${colors.blue[500]};
      background-color: ${colors.blue[500]};
    }
  }
`;

const RadioContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: ${colors.textColor};

  .anticon {
    font-size: 18px;
  }

  span {
    font-weight: 500;
  }
`;

const DropdownSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: ${colors.blue[50]};
  border: 1px solid ${colors.borderColorBase};
  border-radius: 6px;
`;

const DropdownLabel = styled.div`
  .ant-typography {
    font-size: 13px;
    color: ${colors.textColor};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 8px;

  button {
    min-width: 120px;
  }
`;

const ActionInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding-top: 8px;
  border-top: 1px solid ${colors.borderColorBase};

  .ant-typography {
    font-size: 12px;
    color: ${colors.grey[500]};
  }
`;

export default SelectedLeadsBulkEditModal;
